import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { Product } from '../../types/product';
import { Category, CATEGORY_OPTIONS } from '../../types/category';
import api from '../../utils/api';

interface ProductFormData {
  title: string;
  description: string;
  price: number;
  discount: number;
  category: Category;
  imagePath: string;
  secondaryImagesPathes: string[];
  active: boolean;
  priority: number;
  inventory: number;
  isCustomizable: boolean;
  isPromoted: boolean;
}

export const ShopManagement: React.FC = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openModal, setOpenModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductFormData>({
    title: '',
    description: '',
    price: 0,
    discount: 0,
    category: CATEGORY_OPTIONS[0].value,
    imagePath: '',
    secondaryImagesPathes: [],
    active: true,
    priority: 0,
    inventory: 0,
    isCustomizable: false,
    isPromoted: false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [imageError, setImageError] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get<{ data: Product[] }>('/products/all');
      setProducts(response.data.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        ...product,
        active: product.active ?? true,
        priority: product.priority ?? 0,
        secondaryImagesPathes: product.secondaryImagesPathes ?? []
      });
    } else {
      setEditingProduct(null);
      setFormData({
        title: '',
        description: '',
        price: 0,
        discount: 0,
        category: CATEGORY_OPTIONS[0].value,
        imagePath: '',
        secondaryImagesPathes: [],
        active: true,
        priority: 0,
        inventory: 0,
        isCustomizable: false,
        isPromoted: false,
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.imagePath) {
      setImageError(t('admin.products.atLeastOneImageRequired'));
      return;
    }
    try {
      const url = editingProduct
        ? `/products/${editingProduct._id}`
        : '/products';
      const method = editingProduct ? 'put' : 'post';

      await api[method]<{ data: Product }>(url, formData);
      fetchProducts();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('admin.products.confirmDelete'))) {
      try {
        await api.delete(`/products/${id}`);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>, type: 'image' | 'secondary') => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      await handleFileUpload(files[0], type);
    }
  }, []);

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'secondary') => {
    const files = event.target.files;
    if (files && files.length > 0) {
      await handleFileUpload(files[0], type);
    }
  }, []);

  const handleFileUpload = async (file: File, type: 'image' | 'secondary') => {
    if (!file) return;

    try {
      setIsUploading(true);
      setImageError('');
      
      const reader = new FileReader();
      reader.readAsDataURL(file);
      
      reader.onload = async () => {
        const base64String = reader.result as string;
        
        try {
          const response = await api.post<{ url: string }>('/files/upload', {
            image: base64String
          });

          if (response.data && response.data.url) {
            if (type === 'image') {
              setFormData(prev => ({ 
                ...prev, 
                imagePath: response.data.url 
              }));
            } else {
              setFormData(prev => ({ 
                ...prev, 
                secondaryImagesPathes: [...prev.secondaryImagesPathes, response.data.url]
              }));
            }
          } else {
            throw new Error('No file URL received from server');
          }
        } catch (error: any) {
          console.error('Error uploading file:', error);
          setImageError(error.response?.data?.error || t('admin.products.uploadFailed'));
        } finally {
          setIsUploading(false);
        }
      };

      reader.onerror = () => {
        setImageError(t('admin.products.uploadFailed'));
        setIsUploading(false);
      };
    } catch (error) {
      console.error('Error processing file:', error);
      setImageError(t('admin.products.uploadFailed'));
      setIsUploading(false);
    }
  };

  const handleDeleteSecondaryImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      secondaryImagesPathes: prev.secondaryImagesPathes.filter((_, i) => i !== index)
    }));
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4">{t('admin.tabs.shop')}</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
        >
          {t('admin.products.addNew')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.products.title')}</TableCell>
              <TableCell>{t('admin.products.category')}</TableCell>
              <TableCell>{t('admin.products.price')}</TableCell>
              <TableCell>{t('admin.products.discount')}</TableCell>
              <TableCell>{t('admin.products.inventory')}</TableCell>
              <TableCell>{t('admin.products.promoted')}</TableCell>
              <TableCell>{t('admin.products.active')}</TableCell>
              <TableCell>{t('admin.products.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((product) => (
                <TableRow key={product._id}>
                  <TableCell>{product.title}</TableCell>
                  <TableCell>{t(`admin.products.categories.${product.category.toLowerCase()}`)}</TableCell>
                  <TableCell>{product.price}</TableCell>
                  <TableCell>{product.discount}%</TableCell>
                  <TableCell>{product.inventory}</TableCell>
                  <TableCell>{product.isPromoted ? t('common.yes') : t('common.no')}</TableCell>
                  <TableCell>{product.active ? t('common.yes') : t('common.no')}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenModal(product)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(product._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={products.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? t('admin.products.edit') : t('admin.products.addNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('admin.products.title')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={t('admin.products.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label={t('admin.products.category')}
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as Category })}
                  required
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.products.price')}
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: Number(e.target.value) })}
                  required
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.products.discount')}
                  value={formData.discount}
                  onChange={(e) => setFormData({ ...formData, discount: Number(e.target.value) })}
                  required
                  InputProps={{
                    inputProps: { min: 0, max: 100 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.products.inventory')}
                  value={formData.inventory}
                  onChange={(e) => setFormData({ ...formData, inventory: Number(e.target.value) })}
                  required
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label={t('admin.products.priority')}
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: Number(e.target.value) })}
                  InputProps={{
                    inputProps: { min: 0 }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('admin.products.active')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isCustomizable}
                      onChange={(e) => setFormData({ ...formData, isCustomizable: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('admin.products.customizable')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isPromoted}
                      onChange={(e) => setFormData({ ...formData, isPromoted: e.target.checked })}
                      color="primary"
                    />
                  }
                  label={t('admin.products.promoted')}
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDrop={(e) => handleDrop(e, 'image')}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    onChange={(e) => handleFileSelect(e, 'image')}
                  />
                  <label htmlFor="image-upload">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {isUploading ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body1" gutterBottom>
                            {t('admin.products.dragAndDropImage')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('admin.products.orClickToUploadImage')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
                {formData.imagePath && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('admin.products.selectedImage')}
                    </Typography>
                    <TextField
                      fullWidth
                      value={formData.imagePath}
                      onChange={(e) => setFormData({ ...formData, imagePath: e.target.value })}
                      InputProps={{
                        readOnly: true,
                      }}
                    />
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'action.hover',
                    },
                  }}
                  onDrop={(e) => handleDrop(e, 'secondary')}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="secondary-upload"
                    onChange={(e) => handleFileSelect(e, 'secondary')}
                  />
                  <label htmlFor="secondary-upload">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      {isUploading ? (
                        <CircularProgress />
                      ) : (
                        <>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                          <Typography variant="body1" gutterBottom>
                            {t('admin.products.dragAndDropSecondaryImages')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('admin.products.orClickToUploadSecondaryImages')}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </label>
                </Box>
                {imageError && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {imageError}
                  </Typography>
                )}
                {formData.secondaryImagesPathes.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('admin.products.selectedSecondaryImages')}
                    </Typography>
                    <Grid container spacing={2}>
                      {formData.secondaryImagesPathes.map((path, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                          <Box sx={{ position: 'relative' }}>
                            <img
                              src={path}
                              alt={`Secondary image ${index + 1}`}
                              style={{
                                width: '100%',
                                height: '200px',
                                objectFit: 'cover',
                                borderRadius: '4px'
                              }}
                            />
                            <IconButton
                              onClick={() => handleDeleteSecondaryImage(index)}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                }
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingProduct ? t('common.save') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
}; 